import styled from "styled-components";

import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import { ANIMATION_FUNCTION, LinkElement, Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

const COLLAPSE_DURATION_MS = 150;

/** Structure `Modal` defaults to maxHeight 700px — override both height and maxHeight together. */
const IMPACT_REPORT_MODAL_HEIGHT = "min(800px, calc(100vh - 80px))";

/**
 * `height` rather than `maxHeight` alone: the modal's flex column needs a definite height for the
 * body's `overflow-y: auto` to engage. The viewport clamp keeps the footer above the fold on a short screen.
 */
export const IMPACT_REPORT_MODAL_STYLES = {
    content: {
        width: "1040px",
        maxWidth: "calc(100vw - 32px)",
        height: IMPACT_REPORT_MODAL_HEIGHT,
        maxHeight: IMPACT_REPORT_MODAL_HEIGHT,
        borderRadius: "8px",
        overflow: "hidden",
        padding: 0,
    },
} as const;

export const StyledTabs = styled(Tabs)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

export const StyledTabList = styled(TabList)`
    display: flex;
    flex-shrink: 0;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 4px 4px 0 0;
    background-color: ${colorsPalettes.carbon[0]};
`;

/**
 * The selected-state underline is the design-system `Tab`'s own `::after`; this only sizes the tab.
 * A second `border-bottom` here would draw a competing indicator on the same edge.
 */
export const StyledTab = styled(Tab)`
    && {
        flex: 0 0 auto;
        min-width: 176px;
        padding: 12px 16px;
        cursor: pointer;
    }

    &&:hover {
        background-color: ${rgba(colorsPalettes.carbon[500], 0.05)};
    }
`;
StyledTab.tabsRole = "Tab";

export const StyledTabPanel = styled(TabPanel)`
    min-height: 0;
    overflow: hidden;

    &.selected {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }
`;

export const AssetListWrapper = styled(Stack)`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 0 0 8px 8px;
    background-color: ${colorsPalettes.bluegrey[100]};
`;

export const AssetRowWrapper = styled(Stack)`
    /* The list is a flex column; without this it shrinks rows to fit instead of scrolling past them. */
    flex-shrink: 0;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 12px;
    overflow: hidden;
    background-color: ${colorsPalettes.carbon[0]};
`;

/**
 * The whole header is the toggle: full width, so hovering or clicking anywhere on the row expands it,
 * and the owner block rides inside the button. The explicit `aria-label` overrides the button's text
 * content, so pulling the owner name in does not pollute the accessible name.
 */
export const RowToggleButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 44px;
    margin: 0;
    padding: 12px 16px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: ${colorsPalettes.bluegrey[100]};
    }

    /* Negative offset: the row card clips its overflow, so a positive ring would be cut off. */
    &:focus-visible {
        outline: 2px solid ${colorsPalettes.midnight[300]};
        outline-offset: -2px;
    }
`;

export const RowHeaderName = styled.span`
    flex: 1;
    min-width: 0;
`;

export const RowToggleIcon = styled.span<{ $isExpanded: boolean }>`
    display: flex;
    transform: rotate(${({ $isExpanded }) => ($isExpanded ? 0 : -90)}deg);
    transition: transform ${COLLAPSE_DURATION_MS}ms ${ANIMATION_FUNCTION};

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

/**
 * Content appears and disappears instantly — only the chevron animates, matching the prototype. An
 * earlier `grid-template-rows` height transition clipped rows whose chips are measured *after* the
 * track resolved, and animating unbounded-height content is what made long rows feel heavy anyway.
 * Collapsed rows render nothing here (the row component gates the body on `isExpanded`), so there is
 * no hidden subtree to keep out of the focus order or the accessibility tree.
 */
export const CollapsibleRegion = styled.div``;

export const CollapsibleContent = styled.div`
    min-height: 0;
    padding: 0 16px 16px;
`;

export const MappingCard = styled(Stack)`
    flex: 1;
    /* Without this a nowrap chip row sets the card's min-content width and it grows past its flex basis. */
    min-width: 0;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.bluegrey[100]};
`;

export const MappingCardHighlighted = styled(MappingCard)`
    border-color: ${colorsPalettes.blue[200]};
    background-color: ${colorsPalettes.blue[50]};
`;

export const MappingCardBody = styled(Stack)`
    position: relative;
    min-width: 0;
    gap: 8px;
`;

/** Matches the arrow column width in {@link IndustryTransitionRow} so column headers align with the cards. */
export const TransitionRowArrowSpacer = styled.span`
    flex-shrink: 0;
    width: 16px;
`;

export const IndustryTransitionsStack = styled(Stack)`
    min-width: 0;
`;

/** Inline with `body2` subtitle copy — same size as the surrounding sentence, not a separate button row. */
export const StyledLearnMoreLink = styled(LinkElement)`
    ${setFontVariant("body2", colorsPalettes.blue[400])};

    &:hover {
        color: ${colorsPalettes.blue[500]};
        text-decoration: underline;
    }
`;

/**
 * The report callout is informational: a filled blue panel, no icon, block layout with a `role="note"`
 * label. Distinct from the success follow-up notice, which is a neutral task strip with a bell.
 */
export const ReportCrmNoticeBox = styled.div`
    padding: 12px 16px;
    border-radius: 6px;
    background-color: ${colorsPalettes.blue[100]};
`;

export const SuccessFollowUpNoticeBox = styled(Flex)`
    min-height: 56px;
    padding: 8px 16px;
    gap: 8px;
    align-items: center;
    border-radius: 6px;
    background-color: ${colorsPalettes.bluegrey[200]};
`;

export const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

export const SkeletonBar = styled.div<{ $width: string }>`
    width: ${({ $width }) => $width};
    height: 12px;
    border-radius: 4px;
    background-color: ${colorsPalettes.carbon[50]};
`;

export const SkeletonRowHeader = styled(Flex)`
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 44px;
    padding: 12px 16px;
`;

/** A plain box, not `StyledTab`, since the design-system tab reads react-tabs context it would not have here. */
export const SkeletonTab = styled(Flex)`
    flex: 0 0 auto;
    min-width: 176px;
    padding: 12px 16px;
`;

export const SkeletonTabList = styled(Flex)`
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 4px 4px 0 0;
    background-color: ${colorsPalettes.carbon[0]};
`;
